import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import BackofficeLayout from '../../components/backoffice/BackofficeLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form';
import { propertyService } from '../../services/propertyService';
import { cloudinaryService } from '../../services/cloudinaryService';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, Upload, X } from 'lucide-react';

const imageSchema = z.object({
  url: z.string().min(1),
  publicId: z.string().optional(),
});

const propertySchema = z.object({
  titre: z.string().min(5, 'Le titre doit contenir au moins 5 caractères'),
  typeBien: z.string().min(1, 'Type de bien requis'),
  usage: z.string().min(1, 'Usage requis'),
  description: z.string().min(20, 'La description doit contenir au moins 20 caractères'),
  prix: z.string().optional(),
  devise: z.string().default('XOF'),
  prixSurDemande: z.boolean().default(false),
  surface: z.string().min(1, 'La surface est requise'),
  chambres: z.string().optional(),
  sallesDeBain: z.string().optional(),
  statut: z.string().default('Disponible'),
  actif: z.boolean().default(true),
  enVedette: z.boolean().default(false),
  ville: z.string().min(1, 'La ville est requise'),
  zone: z.string().min(1, 'La zone est requise'),
  images: z.array(imageSchema).min(1, 'Au moins une image est requise'),
  ascenseur: z.boolean().default(false),
  climatisation: z.boolean().default(false),
  jardin: z.boolean().default(false),
  piscine: z.boolean().default(false),
  groupeElectrogene: z.boolean().default(false),
  videoSurveillance: z.boolean().default(false),
  titreFoncier: z.boolean().default(false),
  routeGoudronnee: z.boolean().default(false),
});

const PropertyForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const [loading, setLoading] = useState(false);
  const [imageInput, setImageInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const form = useForm({
    resolver: zodResolver(propertySchema),
    defaultValues: {
      titre: '',
      typeBien: '',
      usage: '',
      description: '',
      prix: '',
      devise: 'XOF',
      prixSurDemande: false,
      surface: '',
      chambres: '',
      sallesDeBain: '',
      statut: 'Disponible',
      actif: true,
      enVedette: false,
      ville: '',
      zone: '',
      images: [],
      ascenseur: false,
      climatisation: false,
      jardin: false,
      piscine: false,
      groupeElectrogene: false,
      videoSurveillance: false,
      titreFoncier: false,
      routeGoudronnee: false,
    }
  });

  useEffect(() => {
    if (isEditMode) {
      loadProperty();
    }
  }, [id]);

  const loadProperty = async () => {
    try {
      setLoading(true);
      const property = await propertyService.getPropertyById(id);

      const images = (property.images || []).map(img => {
        if (typeof img === 'string') return { url: img };
        return { url: img.urlImage, publicId: img.cloudinaryPublicId || undefined };
      }).filter(img => img.url);

      form.reset({
        titre: property.titre || '',
        typeBien: property.typeBien || '',
        usage: property.usage || '',
        description: property.description || '',
        prix: property.prix?.toString() || '',
        devise: property.devise || 'XOF',
        prixSurDemande: property.prixSurDemande || false,
        surface: property.surface?.toString() || '',
        chambres: property.chambres?.toString() || '',
        sallesDeBain: property.sallesDeBain?.toString() || '',
        statut: property.statut || 'Disponible',
        actif: property.actif !== undefined ? property.actif : true,
        enVedette: property.enVedette || false,
        ville: property.localisation?.ville || '',
        zone: property.localisation?.zone || '',
        images,
        ascenseur: property.ascenseur || false,
        climatisation: property.climatisation || false,
        jardin: property.jardin || false,
        piscine: property.piscine || false,
        groupeElectrogene: property.groupeElectrogene || false,
        videoSurveillance: property.videoSurveillance || false,
        titreFoncier: property.titreFoncier || false,
        routeGoudronnee: property.routeGoudronnee || false,
      });
    } catch (error) {
      console.error('Error loading property:', error);
      toast.error('Bien non trouvé ou erreur lors du chargement');
      navigate('/backoffice/properties');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);

      const formattedData = {
        titre: data.titre,
        typeBien: data.typeBien,
        usage: data.usage,
        description: data.description,
        devise: data.devise,
        prixSurDemande: data.prixSurDemande,
        surface: parseFloat(data.surface),
        statut: data.statut,
        actif: data.actif,
        enVedette: data.enVedette,
        ville: data.ville,
        zone: data.zone,
        ascenseur: data.ascenseur,
        climatisation: data.climatisation,
        jardin: data.jardin,
        piscine: data.piscine,
        groupeElectrogene: data.groupeElectrogene,
        videoSurveillance: data.videoSurveillance,
        titreFoncier: data.titreFoncier,
        routeGoudronnee: data.routeGoudronnee,
      };

      if (data.prix) formattedData.prix = parseFloat(data.prix);
      if (data.chambres) formattedData.chambres = parseInt(data.chambres);
      if (data.sallesDeBain) formattedData.sallesDeBain = parseInt(data.sallesDeBain);

      let propertyId = id;

      if (isEditMode) {
        await propertyService.updateProperty(id, formattedData);
      } else {
        const created = await propertyService.createProperty(formattedData);
        propertyId = created.id;
      }

      // Synchroniser les images
      if (data.images && data.images.length > 0) {
        await propertyService.syncImages(propertyId, data.images);
      }

      toast.success(isEditMode ? 'Bien modifié avec succès' : 'Bien créé avec succès');
      navigate('/backoffice/properties');
    } catch (error) {
      console.error('Error saving property:', error);
      const message = error.response?.data?.message;
      if (Array.isArray(message)) {
        toast.error(message.join(', '));
      } else {
        toast.error(message || 'Erreur lors de l\'enregistrement');
      }
    } finally {
      setLoading(false);
    }
  };

  const addImageByUrl = () => {
    if (imageInput.trim()) {
      const currentImages = form.getValues('images') || [];
      form.setValue('images', [...currentImages, { url: imageInput.trim() }], { shouldValidate: true });
      setImageInput('');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const currentImages = form.getValues('images') || [];
    let successCount = 0;

    for (const file of files) {
      try {
        const result = await cloudinaryService.uploadImage(file);
        currentImages.push({ url: result.url, publicId: result.publicId });
        successCount++;
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Erreur upload: ${file.name}`);
      }
    }

    form.setValue('images', [...currentImages], { shouldValidate: true });
    if (successCount > 0) {
      toast.success(`${successCount} image(s) uploadée(s)`);
    }
    setUploading(false);
    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index) => {
    const currentImages = form.getValues('images') || [];
    form.setValue('images', currentImages.filter((_, i) => i !== index), { shouldValidate: true });
  };

  if (loading && isEditMode) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600 mx-auto mb-4" />
            <p className="text-slate-600">Chargement...</p>
          </div>
        </div>
      </BackofficeLayout>
    );
  }

  return (
    <BackofficeLayout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/backoffice/properties')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              {isEditMode ? 'Modifier le bien' : 'Nouveau bien'}
            </h1>
            <p className="text-slate-600 mt-1">
              {isEditMode ? 'Modifiez les informations du bien' : 'Ajoutez un nouveau bien à votre catalogue'}
            </p>
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Informations générales */}
            <Card>
              <CardHeader>
                <CardTitle>Informations générales</CardTitle>
                <CardDescription>Détails principaux du bien immobilier</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FormField
                  control={form.control}
                  name="titre"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titre *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Ex: Villa moderne avec piscine" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="typeBien"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Type de bien *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner un type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Villa">Villa</SelectItem>
                            <SelectItem value="Appartement">Appartement</SelectItem>
                            <SelectItem value="Terrain">Terrain</SelectItem>
                            <SelectItem value="Immeuble">Immeuble</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="usage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Usage *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Sélectionner l'usage" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Habitation">Habitation</SelectItem>
                            <SelectItem value="Investissement">Investissement</SelectItem>
                            <SelectItem value="Commercial">Commercial</SelectItem>
                            <SelectItem value="Mixte">Mixte</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="statut"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Statut</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Disponible">Disponible</SelectItem>
                            <SelectItem value="En cours">En cours</SelectItem>
                            <SelectItem value="Vendu">Vendu</SelectItem>
                            <SelectItem value="Loué">Loué</SelectItem>
                            <SelectItem value="Réservé">Réservé</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description *</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Décrivez le bien en détail..."
                          rows={5}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="actif"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Actif</FormLabel>
                          <FormDescription>
                            Le bien sera visible sur le site
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="enVedette"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">En vedette</FormLabel>
                          <FormDescription>
                            Afficher sur la page d'accueil
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Prix et caractéristiques */}
            <Card>
              <CardHeader>
                <CardTitle>Prix et caractéristiques</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="prix"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Prix</FormLabel>
                        <FormControl>
                          <Input {...field} type="number" placeholder="0" />
                        </FormControl>
                        <FormDescription>Laisser vide pour "Sur demande"</FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="devise"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Devise</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="XOF">FCFA (XOF)</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="surface"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Surface (m²) *</FormLabel>
                        <FormControl>
                          <Input {...field} type="number" placeholder="0" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="prixSurDemande"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Prix sur demande</FormLabel>
                        <FormDescription>
                          Afficher "Sur demande" au lieu du prix
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="chambres"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre de chambres</FormLabel>
                        <FormControl>
                          <Input {...field} type="number" placeholder="0" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sallesDeBain"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nombre de salles de bain</FormLabel>
                        <FormControl>
                          <Input {...field} type="number" placeholder="0" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Localisation */}
            <Card>
              <CardHeader>
                <CardTitle>Localisation *</CardTitle>
                <CardDescription>Indiquez la ville et la zone du bien</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="ville"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Ville *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Ex: Dakar, Mbour, Thiès..." />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="zone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Zone / Quartier *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Ex: Almadies, Saly, Plateau..." />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Équipements et prestations */}
            <Card>
              <CardHeader>
                <CardTitle>Équipements et prestations</CardTitle>
                <CardDescription>Cochez les équipements disponibles</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    { name: 'ascenseur', label: 'Ascenseur' },
                    { name: 'climatisation', label: 'Climatisation' },
                    { name: 'jardin', label: 'Jardin' },
                    { name: 'piscine', label: 'Piscine' },
                    { name: 'groupeElectrogene', label: 'Groupe électrogène' },
                    { name: 'videoSurveillance', label: 'Vidéo surveillance' },
                    { name: 'titreFoncier', label: 'Titre foncier' },
                    { name: 'routeGoudronnee', label: 'Route goudronnée' },
                  ].map((item) => (
                    <FormField
                      key={item.name}
                      control={form.control}
                      name={item.name}
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <FormLabel className="text-sm font-medium">{item.label}</FormLabel>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Images */}
            <Card>
              <CardHeader>
                <CardTitle>Images *</CardTitle>
                <CardDescription>Uploadez des images ou ajoutez des URLs. Au moins une image est requise.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Upload fichier */}
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium">Uploader des images</label>
                  <div className="flex gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp,image/jpg"
                      onChange={handleFileUpload}
                      disabled={uploading}
                      className="flex-1 text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100 cursor-pointer"
                    />
                    {uploading && (
                      <div className="flex items-center gap-2 text-sm text-amber-600">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Upload en cours...
                      </div>
                    )}
                  </div>
                </div>

                {/* Séparateur */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-500">ou ajouter par URL</span>
                  </div>
                </div>

                {/* Input URL */}
                <div className="flex gap-2">
                  <Input
                    value={imageInput}
                    onChange={(e) => setImageInput(e.target.value)}
                    placeholder="https://exemple.com/image.jpg"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addImageByUrl())}
                  />
                  <Button type="button" onClick={addImageByUrl} variant="outline">
                    Ajouter
                  </Button>
                </div>

                {/* Prévisualisation des images */}
                {form.watch('images')?.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {form.watch('images').map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image.url}
                          alt={`Image ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border"
                          onError={(e) => { e.target.src = '/placeholder.jpg'; }}
                        />
                        {index === 0 && (
                          <span className="absolute bottom-2 left-2 bg-amber-600 text-white text-xs px-2 py-0.5 rounded">
                            Principale
                          </span>
                        )}
                        {image.publicId && (
                          <span className="absolute bottom-2 right-10 bg-green-600 text-white text-xs px-1.5 py-0.5 rounded">
                            Cloudinary
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {form.formState.errors.images && (
                  <p className="text-sm text-red-500">{form.formState.errors.images.message}</p>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/backoffice/properties')}
                disabled={loading || uploading}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-amber-600 hover:bg-amber-700"
                disabled={loading || uploading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer
                  </>
                )}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </BackofficeLayout>
  );
};

export default PropertyForm;
